import { useState, useEffect } from "react";
import { Eye, Trash2, Phone, Mail, MapPin, Image, Video, Check, X, Clock, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  published: "bg-blue-100 text-blue-800",
};

const statusLabels = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  published: "Publicado",
};

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

export default function AdminContenidoComunitario() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [notes, setNotes] = useState("");
  const [mediaData, setMediaData] = useState({ photos: [], videos: [] });

  useEffect(() => {
    fetchItems();
  }, [statusFilter, page, limit]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("skip", String((page - 1) * limit));
      params.append("limit", String(limit));

      const response = await axios.get(`${API}/admin/community-content?${params}`, {
      });
      setItems(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching items:", error);
      toast.error("Error al cargar el contenido");
    } finally {
      setLoading(false);
    }
  };

  const fetchMediaDetails = async (item) => {
    try {
      const photos = [];
      const videos = [];

      // Fetch photo details
      for (const photoId of (item.photo_ids || [])) {
        try {
          const res = await axios.get(`${API}/media/${photoId}`);
          photos.push(res.data);
        } catch (e) {
          console.error("Error fetching photo:", photoId);
        }
      }

      // Fetch video details
      for (const videoId of (item.video_ids || [])) {
        try {
          const res = await axios.get(`${API}/media/${videoId}`);
          videos.push(res.data);
        } catch (e) {
          console.error("Error fetching video:", videoId);
        }
      }

      setMediaData({ photos, videos });
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  const openDetail = async (item) => {
    setSelectedItem(item);
    setNotes(item.notes || "");
    setMediaData({ photos: [], videos: [] });
    await fetchMediaDetails(item);
  };

  const updateStatus = async (contentId, newStatus, newNotes = null) => {
    try {
      const data = { status: newStatus };
      if (newNotes !== null) data.notes = newNotes;
      
      await axios.put(`${API}/admin/community-content/${contentId}`, data, {
      });
      toast.success(`Contenido ${statusLabels[newStatus].toLowerCase()}`);
      fetchItems();
      setSelectedItem(null);
      setNotes("");
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const deleteItem = async (contentId) => {
    try {
      await axios.delete(`${API}/admin/community-content/${contentId}`, {
      });
      toast.success("Contenido eliminado");
      fetchItems();
      setDeleteDialog(null);
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const pendingCount = items.filter((r) => r.status === "pending").length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-community-content">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Contenido Comunitario
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {pendingCount > 0 ? (
                <span className="text-yellow-600 font-medium">
                  {pendingCount} contenidos pendientes de revisión
                </span>
              ) : (
                `${total} contenidos en total`
              )}
            </p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="approved">Aprobados</SelectItem>
              <SelectItem value="published">Publicados</SelectItem>
              <SelectItem value="rejected">Rechazados</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-yellow-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {items.filter((r) => r.status === "pending").length}
            </p>
            <p className="text-sm text-yellow-700">Pendientes</p>
          </div>
          <div className="bg-green-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-green-600">
              {items.filter((r) => r.status === "approved").length}
            </p>
            <p className="text-sm text-green-700">Aprobados</p>
          </div>
          <div className="bg-blue-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {items.filter((r) => r.status === "published").length}
            </p>
            <p className="text-sm text-blue-700">Publicados</p>
          </div>
          <div className="bg-red-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-red-600">
              {items.filter((r) => r.status === "rejected").length}
            </p>
            <p className="text-sm text-red-700">Rechazados</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Remitente</TableHead>
                <TableHead>Ubicación</TableHead>
                <TableHead>Contenido</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : items.length > 0 ? (
                items.map((item) => (
                  <TableRow key={item.content_id} className={item.status === "pending" ? "bg-yellow-50/50" : ""}>
                    <TableCell>
                      <div className="font-medium text-brand-text">
                        {item.nombre}
                      </div>
                      <div className="text-xs text-gray-400">{item.correo}</div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm text-brand-textSecondary">
                        <MapPin className="h-4 w-4" />
                        {item.municipio}, {item.departamento}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {item.photo_ids?.length > 0 && (
                          <span className="flex items-center gap-1 text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded">
                            <Image className="h-3 w-3" />
                            {item.photo_ids.length}
                          </span>
                        )}
                        {item.video_ids?.length > 0 && (
                          <span className="flex items-center gap-1 text-sm bg-purple-50 text-purple-700 px-2 py-1 rounded">
                            <Video className="h-3 w-3" />
                            {item.video_ids.length}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[item.status] || statusColors.pending}>
                        {statusLabels[item.status] || "Pendiente"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-brand-textSecondary text-sm">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString("es-HN")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDetail(item)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteDialog(item)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Camera className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-brand-textSecondary">No hay contenido comunitario</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalle del Contenido</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4 mt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading text-xl font-bold text-brand-text">
                      {selectedItem.nombre}
                    </h3>
                    <Badge className={`mt-2 ${statusColors[selectedItem.status] || statusColors.pending}`}>
                      {statusLabels[selectedItem.status] || "Pendiente"}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Correo</p>
                    <a href={`mailto:${selectedItem.correo}`} className="font-medium text-primary hover:underline">
                      {selectedItem.correo}
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-500">Teléfono</p>
                    <a href={`tel:${selectedItem.telefono}`} className="font-medium text-primary hover:underline">
                      {selectedItem.telefono}
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-500">Ubicación</p>
                    <p className="font-medium">{selectedItem.municipio}, {selectedItem.departamento}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Fecha de envío</p>
                    <p className="font-medium">
                      {selectedItem.created_at
                        ? new Date(selectedItem.created_at).toLocaleString("es-HN")
                        : "-"}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-gray-500 text-sm mb-2">Descripción</p>
                  <p className="text-brand-text bg-gray-50 p-4 rounded-xl whitespace-pre-wrap">
                    {selectedItem.descripcion}
                  </p>
                </div>

                {/* Photos */}
                {mediaData.photos.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2">Fotos ({mediaData.photos.length})</p>
                    <div className="grid grid-cols-3 gap-2">
                      {mediaData.photos.map((photo, i) => (
                        <a
                          key={i}
                          href={getImageUrl(photo.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity"
                        >
                          <img
                            src={getImageUrl(photo.url)}
                            alt={photo.title || `Foto ${i + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Videos */}
                {mediaData.videos.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2">Videos ({mediaData.videos.length})</p>
                    <div className="space-y-2">
                      {mediaData.videos.map((video, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Video className="h-5 w-5 text-purple-500" />
                            <span className="text-sm">{video.title || video.filename}</span>
                          </div>
                          <a
                            href={getImageUrl(video.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline"
                          >
                            Ver video
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-gray-500 text-sm mb-2">Notas internas</p>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Agregar notas sobre este contenido..."
                    className="min-h-24"
                  />
                </div>

                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedItem.content_id, "approved", notes)}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Aprobar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedItem.content_id, "published", notes)}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Publicar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateStatus(selectedItem.content_id, "rejected", notes)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Rechazar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminar Contenido</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar el contenido de "{deleteDialog?.nombre}"? Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={() => deleteItem(deleteDialog?.content_id)}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="contenido-comunitario-pagination"
          label="envíos"
        />
    </AdminLayout>
  );
}
